import { Component, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { BillService } from '../../services/bill.service';
import { HierarchyMeterRowComponent } from './hierarchy-meter-row.component';
import { MatchStatusBadgeComponent } from '../shared/match-status-badge.component';
import { ColumnKey, BillGroup } from '../../models';

@Component({
  selector: 'app-hierarchy-address-row',
  standalone: true,
  imports: [
    DecimalPipe,
    MatCheckboxModule,
    MatchStatusBadgeComponent,
    HierarchyMeterRowComponent
],
  template: `
      <div
        class="address-row"
        [class.drag-over]="isDragOver"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
        >
        <div class="address-header" (click)="toggleExpand($event)">
          <div class="address-info">
            <div
              class="drag-handle"
              draggable="true"
              (dragstart)="onDragStart($event)"
              (click)="$event.stopPropagation()"
              >
              <i class="material-symbols-rounded">drag_indicator</i>
            </div>
            <mat-checkbox
              [checked]="billService.selectedAddressIds().includes(address.id)"
              (change)="toggleSelection()"
              (click)="$event.stopPropagation()"
            ></mat-checkbox>
            <button class="expand-btn" (click)="toggleExpand($event)" [class.collapsed]="!expanded">
              <i class="material-symbols-rounded">expand_more</i>
            </button>
            <div class="address-icon-wrapper">
              <i class="material-symbols-rounded address-icon">home_work</i>
            </div>
            <div class="address-details">
              <p class="address-name">
                @if (address.isLoading && isUnknownAddress()) {
                  <span class="loading-text">
                    Loading bill details
                    <i class="material-symbols-rounded spinner">sync</i>
                  </span>
                } @else {
                  {{ address.name }}
                  @if (isUnknownAddress()) {
                    <span class="unknown-label">Unknown Address</span>
                  }
                  @if (!isUnknownAddress()) {
                    <span class="location-text">, {{ address.city }} {{ address.state }}</span>
                  }
                }
              </p>
            </div>
      
            <app-match-status-badge [status]="'NEW'"></app-match-status-badge>
          </div>

          <div class="address-stats">
            <span class="stat"><i class="material-symbols-rounded">speed</i> {{ meterCount }}</span>
            <span class="stat"><i class="material-symbols-rounded">description</i> {{ billCount }}</span>
            @if (address.metrics.electric > 0) {
              <span class="stat usage">{{ address.metrics.electric | number }} kWh</span>
            }
            @if (address.metrics.gas > 0) {
              <span class="stat usage">{{ address.metrics.gas | number }} DTH</span>
            }
          </div>
        </div>
      
        <!-- Meters List (Children) -->
        <div class="meters-container" [class.expanded]="expanded">
          <div class="meters-content">
            @for (meter of getMeters(); track meter) {
              <app-hierarchy-meter-row
                [meter]="meter"
                [visibleColumns]="visibleColumns"
                [sortConfig]="sortConfig"
                (openPairReview)="openPairReview.emit($event)"
              ></app-hierarchy-meter-row>
            }
          </div>
        </div>
      </div>
      `,
  styleUrl: './hierarchy-address-row.component.scss'
})
export class HierarchyAddressRowComponent implements OnChanges {
    @Input() address: any;
    @Input() visibleColumns: Record<ColumnKey, boolean> = {} as any;
    @Input() sortConfig: any;
    @Output() openPairReview = new EventEmitter<BillGroup>();

    expanded = true;
    isDragOver = false;

    // Pre-computed stats
    meterCount = 0;
    billCount = 0;
    meters: any[] = [];

    constructor(public billService: BillService) {}

    ngOnChanges() {
        this.updateComputedValues();
    }

    private updateComputedValues() {
        this.meters = Object.values(this.address.meters || {});
        this.meterCount = this.meters.length;
        this.billCount = this.meters.reduce((sum: number, m: any) =>
            sum + (m.bills?.length || 0), 0);
    }

    isUnknownAddress(): boolean {
        return !this.address.name?.trim() || this.address.name === 'Unknown Address';
    }

    getMeters() {
        return this.meters;
    }

    toggleExpand(e: Event) {
        if ((e.target as HTMLElement).tagName !== 'INPUT') {
            this.expanded = !this.expanded;
            e.stopPropagation();
        }
    }

    toggleSelection() {
        this.billService.toggleSelection(this.address.id, 'address');
    }

    onDragStart(e: DragEvent) {
        e.dataTransfer?.setData('address-id', this.address.id);
        if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
        e.stopPropagation();
    }

    onDragOver(e: DragEvent) {
        e.preventDefault();
        // Accept bills and meters (meter-id contains JSON with bill IDs)
        if (e.dataTransfer?.types.includes('bill-id') || e.dataTransfer?.types.includes('meter-id')) {
            this.isDragOver = true;
        }
    }

    onDragLeave(e: DragEvent) {
        this.isDragOver = false;
    }

    onDrop(e: DragEvent) {
        e.preventDefault();
        this.isDragOver = false;

        // Handle bill being dropped onto this address
        const billId = e.dataTransfer?.getData('bill-id');
        if (billId) {
            this.billService.moveBillToAddress(billId, this.address.id);
            return;
        }

        // Handle meter being dropped onto this address (moves all its bills)
        const meterData = e.dataTransfer?.getData('meter-id');
        if (meterData) {
            try {
                const { meterId, billIds } = JSON.parse(meterData);
                this.billService.moveMeterToAddress(meterId, billIds, this.address.id);
            } catch (err) {
                console.error('Failed to parse meter-id data', err);
            }
        }
    }
}
